/**
 * AI Trade Analyst Hook
 * 
 * Consolidated hook for all AI analyst functionality:
 * - Settings (model selection)
 * - Insights (auto-generated recommendations)
 * - Chat (interactive Q&A)
 */

import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../services/api-client';

// ============================================================================
// Types
// ============================================================================

export interface AIModel {
  id: string;
  name: string;
  description: string;
  isDefault?: boolean;
  estimatedSeconds?: number;
  requiresStreaming?: boolean;
}

export interface AISettings {
  preferredModel: string;
  isConfigured: boolean;
}

export interface AIInsight {
  id: string;
  category: 'strategy_adjustment' | 'risk_warning' | 'optimization' | 'portfolio_action';
  title: string;
  summary: string;
  recommendation: string;
  dataBasis: string;
  estimatedImpact?: string;
}

/** New v2 API response format - prose-based insights */
export interface InsightsResponseV2 {
  response: string;  // Prose text from AI (Finance Bro style)
  dateRange: { start: string; end: string; label: string };
  tradeCount: number;
  model: string;
  estimatedSeconds?: number;
  tokenUsage?: { prompt: number; completion: number };
}

/** Legacy v1 format for backward compatibility */
export interface InsightsResponseV1 {
  context: {
    timeRange: { start: string; end: string; label: string };
    totalTrades: number;
    closedTrades: number;
  };
  insights: AIInsight[];
  tokenUsage?: { prompt: number; completion: number };
}

/** Union type - API may return either format */
export type InsightsResponse = InsightsResponseV2 | InsightsResponseV1;

/** Type guard to check which format we received */
export function isV2Response(data: InsightsResponse): data is InsightsResponseV2 {
  return 'response' in data && typeof data.response === 'string';
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  model?: string;
}

export type TimeRange = 'today' | 'week' | 'month' | 'all';

export type CustomDateRange = { from: Date; to: Date } | null;

// ============================================================================
// API Functions
// ============================================================================

async function fetchModels(): Promise<AIModel[]> {
  const response = await apiClient.get('/api/ai/models');
  const data = await apiClient.handleResponse(response);
  return data.models || [];
}

async function fetchSettings(): Promise<AISettings> {
  const response = await apiClient.get('/api/ai/settings');
  const data = await apiClient.handleResponse(response);
  return data.settings;
}

async function updateSettings(settings: { preferredModel?: string }): Promise<void> {
  const response = await apiClient.put('/api/ai/settings', settings);
  await apiClient.handleResponse(response);
}

async function fetchInsights(args: {
  timeRange: TimeRange;
  customDateRange: CustomDateRange;
  deepThinking: boolean;
}): Promise<InsightsResponse> {
  const params = new URLSearchParams();
  params.set('timeRange', args.timeRange);
  if (args.customDateRange) {
    params.set('startDate', args.customDateRange.from.toISOString());
    params.set('endDate', args.customDateRange.to.toISOString());
  }
  if (args.deepThinking) {
    params.set('deepThinking', 'true');
  }

  const response = await apiClient.get(`/api/ai/insights?${params.toString()}`);
  const data = await apiClient.handleResponse(response);
  return data;
}

async function sendChatMessage(
  message: string, 
  history: { role: 'user' | 'assistant'; content: string }[],
  customDateRange: CustomDateRange
): Promise<{ response: string; model?: string; tokenUsage?: { prompt: number; completion: number } }> {
  const body: any = { message, history };
  if (customDateRange) {
    body.startDate = customDateRange.from.toISOString();
    body.endDate = customDateRange.to.toISOString();
  }
  const response = await apiClient.post('/api/ai/chat', body);
  const data = await apiClient.handleResponse(response);
  return data;
}

// ============================================================================
// Hook
// ============================================================================

export function useAI() {
  const queryClient = useQueryClient();
  
  // Local state for chat
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [timeRange, setTimeRange] = useState<TimeRange>('today');
  const [customDateRange, setCustomDateRange] = useState<CustomDateRange>(null);
  const [deepThinking, setDeepThinking] = useState(false); // session-based

  // -------------------------------------------------------------------------
  // Settings Queries
  // -------------------------------------------------------------------------
  
  const modelsQuery = useQuery({
    queryKey: ['ai', 'models'],
    queryFn: fetchModels,
    staleTime: 60 * 60 * 1000, // 1 hour - models rarely change
  });

  const settingsQuery = useQuery({
    queryKey: ['ai', 'settings'],
    queryFn: fetchSettings,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const updateSettingsMutation = useMutation({
    mutationFn: updateSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai', 'settings'] });
    },
  });

  // -------------------------------------------------------------------------
  // Insights Query
  // -------------------------------------------------------------------------

  const insightsQuery = useQuery({
    queryKey: [
      'ai',
      'insights',
      timeRange,
      customDateRange?.from.toISOString() ?? null,
      customDateRange?.to.toISOString() ?? null,
      deepThinking,
    ],
    queryFn: () => fetchInsights({ timeRange, customDateRange, deepThinking }),
    staleTime: 5 * 60 * 1000, // 5 minutes cache
    enabled: settingsQuery.data?.isConfigured !== false,
  });

  // -------------------------------------------------------------------------
  // Chat Mutation
  // -------------------------------------------------------------------------

  const chatMutation = useMutation({
    mutationFn: ({ message, history }: { 
      message: string; 
      history: { role: 'user' | 'assistant'; content: string }[] 
    }) => sendChatMessage(message, history, customDateRange),
  });

  // -------------------------------------------------------------------------
  // Chat Handlers
  // -------------------------------------------------------------------------

  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim()) return;

    // Add user message immediately
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: content.trim(),
      timestamp: new Date(),
    };
    
    setMessages(prev => [...prev, userMessage]);
    setIsTyping(true);

    try {
      // Build history for API (exclude IDs and timestamps)
      const history = messages.map(m => ({ role: m.role, content: m.content }));
      
      const result = await chatMutation.mutateAsync({ 
        message: content.trim(), 
        history 
      });

      // Add assistant message
      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: result.response,
        model: result.model,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      // Add error message
      const errorMessage: ChatMessage = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  }, [messages, chatMutation]);

  const clearChat = useCallback(() => {
    setMessages([]);
  }, []);

  // -------------------------------------------------------------------------
  // Return API
  // -------------------------------------------------------------------------

  return {
    // Settings
    models: modelsQuery.data || [],
    modelsLoading: modelsQuery.isLoading,
    settings: settingsQuery.data,
    settingsLoading: settingsQuery.isLoading,
    updateModel: (model: string) => updateSettingsMutation.mutate({ preferredModel: model }),
    isUpdatingModel: updateSettingsMutation.isPending,

    // Insights
    insights: insightsQuery.data,
    insightsLoading: insightsQuery.isLoading,
    insightsError: insightsQuery.error,
    timeRange,
    setTimeRange,
    customDateRange,
    setCustomDateRange,
    deepThinking,
    setDeepThinking,
    refetchInsights: () => insightsQuery.refetch(),

    // Chat
    messages,
    isTyping,
    sendMessage,
    clearChat,
    isSendingMessage: chatMutation.isPending,

    // Status
    isConfigured: settingsQuery.data?.isConfigured ?? false,
  };
}

